import React, { useState, useRef } from 'react';
import RegistrationForm from '../components/RegistrationForm';
import { Payment } from '@mercadopago/sdk-react';
import { getInitialization, customization, onSubmit, onError, onReady, getLoteCorrente } from '../services/paymentService';
import { getEventoAtual } from '../config/event';
import './LandingPage.css';

const formatBRL = (v: number) => `R$\u00A0${v.toFixed(2).replace('.', ',')}`;

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const fmtCurto = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')} ${MESES[d.getMonth()]}`;
};

const periodoLote = (lote: { inicio: string; fim: string }, idx: number, total: number) => {
  if (idx === 0) return `Até ${fmtCurto(lote.fim)}`;
  if (idx === total - 1) return `De ${fmtCurto(lote.inicio)} até o espetáculo`;
  return `${fmtCurto(lote.inicio)} — ${fmtCurto(lote.fim)}`;
};

const LandingPage: React.FC = () => {
  const evento = getEventoAtual();
  const lote = getLoteCorrente();
  const lotes = evento.lotes || [];

  const [, setTicketCount] = useState(1);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [qrCode, setQrCode] = useState<string>('');
  const [qrCodeBase64, setQrCodeBase64] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);

  const bookingSectionRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToEl = (el: HTMLElement | null) => {
    if (!el) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  const scrollToBooking = () => {
    setIsFormVisible(true);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => {
      bookingSectionRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    }, 50);
  };

  const copyToClipboard = () => {
    if (!qrCode) return;
    navigator.clipboard.writeText(qrCode).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  const handleFormSubmit = (data: any) => {
    setFormData(data);
    setIsFormSubmitted(true);
    setIsLoading(true);
    // Mira no widget de pagamento (Etapa 2), não no topo da reserva,
    // senão o usuário volta p/ o formulário e não vê onde pagar.
    setTimeout(() => {
      scrollToEl(paymentRef.current);
      paymentRef.current?.focus({ preventScroll: true });
    }, 100);
  };

  const handlePaymentReady = () => {
    setIsLoading(false);
    // O Brick do MP muda a altura ao carregar; reancora no widget.
    setTimeout(() => {
      scrollToEl(paymentRef.current);
    }, 100);
  };

  const handlePaymentSuccess = (paymentData: { qr_code: string; qr_code_base64: string }) => {
    setOrderConfirmed(true);
    setIsFormSubmitted(false);
    setQrCode(paymentData.qr_code);
    setQrCodeBase64(paymentData.qr_code_base64);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => {
      bookingSectionRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    }, 100);
  };

  const handleReinitializePayment = (updatedFormData: any) => {
    setFormData(updatedFormData);
    if (isFormSubmitted) {
      setIsFormSubmitted(false);
      setTimeout(() => {
        setIsFormSubmitted(true);
        handleFormSubmit(updatedFormData);
      }, 0);
      getInitialization(updatedFormData);
    }
  };

  return (
    <div className="era-landing">
      <header className="era-header">
        <div className="era-header-inner">
          <a href="https://espacoera.store/" className="era-brand-link" title="Espaço ERA — Página Principal">
            <img src="/logo-era.jpg" alt="Logo Espaço ERA" className="era-header-logo" />
          </a>
          <button
            type="button"
            className="era-nav-toggle"
            aria-expanded={isMenuOpen}
            aria-controls="era-nav-principal"
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            {isMenuOpen ? 'Fechar' : 'Menu'}
          </button>
          <nav id="era-nav-principal" className={`era-nav${isMenuOpen ? ' is-open' : ''}`} aria-label="Navegação principal">
            <a href="#espetaculo" className="era-nav-link" onClick={() => setIsMenuOpen(false)}>O espetáculo</a>
            <a href="#lotes" className="era-nav-link" onClick={() => setIsMenuOpen(false)}>Ingressos</a>
            <a href="/meus-ingressos" className="era-nav-link" onClick={() => setIsMenuOpen(false)}>Meus ingressos</a>
          </nav>
        </div>
      </header>

      {/* Cartaz */}
      <section id="espetaculo" className="era-hero" aria-labelledby="hero-title">
        <div className="era-wrap era-hero-grid">
          <div className="era-hero-top">
            <p className="era-folio">29 de novembro de 2026</p>
            <h1 id="hero-title" className="era-display">
              <span className="era-display-line">Mitologia</span>
              <span className="era-display-line">grega</span>
              <span className="era-display-sub">pelas mulheres dessa ERA</span>
            </h1>
          </div>
          <figure className="era-hero-figure">
            <img
              src="/era-mitologia-1.jpeg"
              alt="Arte do espetáculo"
              className="era-hero-img"
              loading="eager"
            />
          </figure>
          <div className="era-hero-bottom">
            <div className="era-hero-info">
              <div>
                <p className="era-hora">19H00</p>
                <p className="era-rotulo">Início</p>
                <p className="era-horario-sec">Abertura 18H30</p>
              </div>
              <div>
                <p className="era-local-nome">Teatro UMC</p>
                <p className="era-local-end">Av. Imperatriz Leopoldina, 550 · Unicid</p>
              </div>
              <div className="era-hero-ticket">
                <p className="era-rotulo">Ingressos</p>
                <p className="era-ticket-preco">
                  {lote.nome} — <span className="era-preco">inteira {formatBRL(lote.inteira)}</span> · <span className="era-preco">meia {formatBRL(lote.meia)}</span>
                </p>
                <p className="era-ticket-ate">até {fmtCurto(lote.fim).toLowerCase()}</p>
                <button type="button" onClick={scrollToBooking} className="era-primary-btn">
                  Garantir ingresso
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre + compra */}
      <section className="era-sobre" aria-labelledby="sobre-title">
        <div className="era-wrap era-sobre-grid">
          <div>
            <h2 id="sobre-title" className="era-sobre-title">Sobre o espetáculo</h2>
            <p className="era-sobre-text">
              A mitologia grega segue encantando e inspirando grandes obras de arte.
              O olhar a partir do sagrado e do profano, que se misturam, das divindades
              cultuadas (mesmo nas suas imperfeições). Nosso espetáculo se inspira no
              clássico, mas é escrito e produzido por deusas e deuses dessa ERA. Do Olimpo,
              dos mitos e ao submundo, um convite para aflorar a divindade que existe
              dentro de nós.
            </p>
            <p className="era-sobre-aviso">
              Esse espetáculo pode ter algumas apresentações com luzes não recomendadas
              para pessoas foto sensíveis.
            </p>
          </div>
          {!orderConfirmed && (
            <aside className="era-sobre-cta">
              <p className="era-sobre-preco">
                {lote.nome} — <span className="era-preco">inteira {formatBRL(lote.inteira)}</span> · <span className="era-preco">meia {formatBRL(lote.meia)}</span>
              </p>
              <button type="button" onClick={scrollToBooking} className="era-primary-btn">
                Garantir ingresso
              </button>
            </aside>
          )}
        </div>
      </section>

      {/* Ingressos */}
      <section id="lotes" className="era-ingressos" aria-labelledby="ingressos-title">
        <div className="era-wrap">
          <h2 id="ingressos-title" className="era-h2">Ingressos</h2>
          <div className="era-tabela-wrap">
            <table className="era-tabela">
              <caption className="era-tabela-caption era-visually-hidden">Inteira e meia entrada por lote</caption>
              <thead>
                <tr>
                  <th scope="col">Lote</th>
                  <th scope="col">Inteira</th>
                  <th scope="col">Meia</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((l, idx) => {
                  const vigente = l.id === lote.id;
                  return (
                    <tr key={l.id} className={vigente ? 'era-tabela-atual' : undefined} aria-current={vigente ? true : undefined}>
                      <th scope="row">
                        {l.nome}
                        <span className="era-tabela-periodo">{periodoLote(l, idx, lotes.length)}</span>
                        {vigente && <span className="era-tabela-vigente"> · lote vigente</span>}
                      </th>
                      <td>{formatBRL(l.inteira)}</td>
                      <td>{formatBRL(l.meia)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button type="button" onClick={scrollToBooking} className="era-cta-line">
              Garantir ingresso no {lote.nome}
            </button>
          </div>
        </div>
      </section>

      {/* Reserva */}
      {(isFormVisible || isFormSubmitted || orderConfirmed) && (
      <section id="ingressos-compra" ref={bookingSectionRef} className="era-reserva" aria-labelledby="reserva-title" tabIndex={-1}>
        <div className="era-wrap">
          {!orderConfirmed && (
            <div className="era-reserva-head">
              <h2 id="reserva-title" className="era-h2">Reserva</h2>
              <p className="era-reserva-linha">
                {lote.nome} — <span className="era-preco">inteira {formatBRL(lote.inteira)}</span> · <span className="era-preco">meia {formatBRL(lote.meia)}</span>.
              </p>
            </div>
          )}

          {isFormVisible && !orderConfirmed && (
            <div className="booking-flow-container">
              <RegistrationForm
                eventPrice={lote.inteira}
                onTicketCountChange={setTicketCount}
                onFormSubmit={handleFormSubmit}
                isLoading={isLoading}
                onAddTicket={handleReinitializePayment}
              />

              {isFormSubmitted && formData && (
                <div className="payment-brick-wrapper" ref={paymentRef} tabIndex={-1}>
                  <div className="brick-header">
                    <h3 className="brick-title">Pagamento via Mercado Pago</h3>
                    <p className="brick-subtitle">
                      Pix com aprovação imediata ou cartão de crédito.
                    </p>
                  </div>
                  <div className="brick-embed">
                    <Payment
                      initialization={getInitialization(formData)}
                      customization={customization}
                      onSubmit={async (param) => {
                        const paymentData = await onSubmit({ formData }, param, () => {});
                        handlePaymentSuccess(paymentData);
                      }}
                      onReady={() => {
                        onReady();
                        handlePaymentReady();
                      }}
                      onError={onError}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {orderConfirmed && (
            <div className="order-success-card">
              <h2 className="success-title">Pedido registrado</h2>
              <p className="success-message">
                Se você escolheu Pix, conclua abaixo com o QR Code ou o copia-e-cola.
                Assim que o pagamento for confirmado, os ingressos em PDF chegam no seu e-mail.
              </p>

              <div className="pix-box">
                <div className="pix-qr-area">
                  {qrCodeBase64 ? (
                    <img
                      src={`data:image/png;base64,${qrCodeBase64}`}
                      alt="QR Code Pix do pedido"
                      className="pix-qr-image"
                    />
                  ) : (
                    <div className="pix-qr-placeholder">Carregando QR Code...</div>
                  )}
                </div>

                <div className="pix-copy-area">
                  <label htmlFor="pixCodeInput" className="pix-label">
                    Pix copia-e-cola
                  </label>
                  <div className="pix-input-group">
                    <input
                      type="text"
                      id="pixCodeInput"
                      className="pix-input"
                      value={qrCode}
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="pix-copy-btn"
                    >
                      {copySuccess ? 'Copiado' : 'Copiar código'}
                    </button>
                  </div>
                  <p className="pix-tip">
                    No app do banco, escolha Pix copia-e-cola e cole o código acima.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      <div className="era-meandro" aria-hidden="true" />

      <footer className="era-footer">
        <div className="era-wrap era-footer-grid">
          <div className="era-footer-col">
            <a href="#espetaculo">O espetáculo</a>
            <a href="#lotes">Ingressos</a>
            <a href="/meus-ingressos">Meus ingressos</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
